<script lang="ts">
import { computed, defineComponent, nextTick, ref, unref, watch } from 'vue';

import { classesStates } from '@components/utils';

import {
  CdsTransition,
  makeDisabledProps,
  makeFieldProps,
  makeFocusProps,
  makeIconEvents,
  makeIconProps,
  makeInputProps,
  makeLoadingProps,
  makeReadonlyProps,
  makeSizeProps,
  makeThemeProps,
  makeValidationProps,
  provideTheme,
  useField,
  useFilteredInputAttrs,
  useFilteredInputProps,
  useFocus,
  useIcon,
  useInput,
  useLoading,
  useProxyModel
} from '../../composable';
import { CdsIcon } from '../CdsIcon';
import { CdsInput, CdsInputDescription, CdsInputLabel, CdsInputCounter } from '../CdsInput';
import { CdsLoader } from '../CdsLoader';
import { CdsFunctionalButton } from '../CdsButton';

export const componentName = 'CdsTextInput';
export default defineComponent({
  name: componentName,
  components: {
    CdsInput,
    CdsInputLabel,
    CdsInputDescription,
    CdsInputCounter,
    CdsIcon,
    CdsLoader,
    CdsFunctionalButton,
    CdsTransition
  },
  inheritAttrs: false,
  props: {
    ...makeFieldProps(),
    ...makeInputProps(),
    ...makeValidationProps(),
    ...makeIconProps(),
    ...makeIconEvents(),
    ...makeFocusProps(),
    ...makeDisabledProps(),
    ...makeReadonlyProps(),
    ...makeLoadingProps(),
    ...makeSizeProps(),
    ...makeThemeProps()
  },
  emits: {
    /**
     * Событие при фокусе
     * @type { boolean }
     */
    'update:focused': (value: boolean) => true,
    /**
     * Событие при изменении значения (v-model)
     * @type { string }
     */
    'update:modelValue': (value: string) => true,
    /**
     * Событие очистки поля ввода
     */
    'click:clear': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке перед значением поля
     */
    'click:prepend': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке после значения поля
     */
    'click:append': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке внутри элемента перед значением поля
     */
    'click:prependInner': (event: MouseEvent) => true,
    /**
     * Событие клика по иконке внутри элемента после значения поля
     */
    'click:appendInner': (event: MouseEvent) => true,
    /**
     * Событие клика по полю ввода
     */
    'click:control': (event: MouseEvent) => true,
    /**
     * Событие нажатия клавиши мыши на поле ввода
     */
    'mousedown:control': (event: MouseEvent) => true
  },
  setup(props, { attrs, emit, slots, expose }) {
    const model = useProxyModel(props, 'modelValue');
    const { id, hasLabel, hasDescription } = useInput(props, slots, componentName);
    const { hasPrepend, hasAppend, iconClasses } = useIcon(props, slots, componentName);
    const { hasLoading, loadingClasses } = useLoading(props);
    const { isFocused, focus, blur } = useFocus(props, componentName);
    const {
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      fieldClasses,
      fieldProps,
      handleClickAppendInner,
      handleClickPrependInner
    } = useField(props, slots, componentName);
    const { inputProps } = useFilteredInputProps(props, [...fieldProps, 'modelValue', 'focused', 'loading', 'theme']);
    const { rootAttrs, inputAttrs } = useFilteredInputAttrs(attrs);
    const hasClear = computed(() => props.clearable);
    const counterValue = computed(() => (unref(model) ?? '').toString().length);
    const { themeClasses } = provideTheme(props);

    const fieldRef = ref<HTMLElement>();
    const inputRef = ref<HTMLInputElement>();
    const validationRef = ref<typeof CdsInput>();

    watch(
      () => props.value,
      (value) => {
        if (value) {
          model.value = value;
        }
      },
      { immediate: true }
    );

    function onInput(event: Event) {
      model.value = (event.target as HTMLInputElement).value;
    }

    function onFocus() {
      if (props.disabled) return;

      if (unref(inputRef) !== document.activeElement) {
        unref(inputRef)?.focus();
      }

      if (!unref(isFocused)) focus();
    }

    function onClear(event: MouseEvent) {
      event.stopPropagation();

      onFocus();

      nextTick(() => {
        model.value = null;

        emit('click:clear', event);
      });
    }

    function onControlMousedown(event: MouseEvent) {
      emit('mousedown:control', event);

      if (event.target === unref(inputRef)) return;

      onFocus();
      event.preventDefault();
    }

    function onControlClick(event: MouseEvent) {
      onFocus();

      emit('click:control', event);
    }

    expose({
      fieldRef,
      inputRef,
      validationRef,
      focus: onFocus,
      blur
    });

    return {
      id,
      model,
      fieldRef,
      inputRef,
      validationRef,
      isFocused,
      rootAttrs,
      inputAttrs,
      inputProps,
      hasLabel,
      hasDescription,
      hasPrepend,
      hasAppend,
      hasPrependInnerIcon,
      hasAppendInnerIcon,
      hasLoading,
      hasClear,
      counterValue,
      iconClasses,
      themeClasses,
      fieldClasses,
      loadingClasses,
      classesStates,
      onBlur: blur,
      onFocus,
      onInput,
      onClear,
      onControlClick,
      onControlMousedown,
      handleClickPrependInner,
      handleClickAppendInner
    };
  }
});
</script>

<template>
  <div ref="fieldRef" class="cds-text-field" :class="themeClasses" v-bind="rootAttrs">
    <cds-input-label v-if="hasLabel" :for="id" :text="label" :disabled="disabled">
      <!--
        @slot Лейбл
        @type string | html
      -->
      <slot name="label" />
    </cds-input-label>
    <cds-input-description v-if="hasDescription" :text="description">
      <!--
        @slot Описание
        @type string | html
      -->
      <slot name="description" />
    </cds-input-description>
    <cds-input
      :id="id"
      ref="validationRef"
      v-model="model"
      :focused="isFocused"
      class="cds-text-input"
      :class="[fieldClasses, iconClasses, loadingClasses]"
      v-bind="{ ...inputProps }"
      @click:control="onControlClick"
      @mousedown:control="onControlMousedown"
    >
      <template v-if="hasPrepend" #prepend>
        <!--
          @slot Элемент перед значением поля
          @type string | html
        -->
        <slot name="prepend" />
      </template>
      <template #default="{ isDisabled, isReadonly, isValid }">
        <div v-if="hasPrependInnerIcon" class="cds-text-input__prepend-inner" @click="handleClickPrependInner">
          <!--
            @slot Элемент внутри перед значением поля
            @type string | html
          -->
          <cds-icon v-if="!!prependInnerIcon" :name="prependInnerIcon" :size="size" />
          <slot name="prepend-inner" />
        </div>
        <span v-if="prefix" class="cds-text-input__prefix" @click="onFocus">{{ prefix }}</span>
        <div class="cds-text-input__input" data-no-activator="">
          <slot />
          <input
            :id="id"
            ref="inputRef"
            :type="type"
            :value="model"
            :name="name"
            :placeholder="placeholder"
            :disabled="isDisabled.value"
            :readonly="isReadonly.value || loading"
            :aria-disabled="isReadonly.value || isDisabled.value"
            :aria-invalid="error || isValid.value === false"
            :autofocus="autofocus"
            :maxlength="limit ? +limit : undefined"
            v-bind="{ ...inputAttrs }"
            @input="onInput"
            @focus="onFocus"
            @blur="onBlur"
          />
        </div>
        <div v-if="hasClear && !hasLoading" class="cds-text-input__clear">
          <cds-functional-button
            :disabled="isDisabled.value"
            :title="clearLabel"
            icon="remove"
            class="cds-text-input__clear--button"
            :class="{ [classesStates.active]: !!model || persistentClearable }"
            @click="onClear"
            @mousedown.prevent.stop
          />
        </div>
        <span v-if="suffix" class="cds-text-input__suffix" @click="onFocus">{{ suffix }}</span>
        <div
          v-if="hasAppendInnerIcon || hasLoading"
          class="cds-text-input__append-inner"
          @click="handleClickAppendInner"
        >
          <template v-if="hasLoading">
            <cds-transition transition="fade-transition" role="alert" aria-busy="true">
              <cds-loader size="xs" class="cds-text-input__loader" />
            </cds-transition>
          </template>
          <!--
            @slot Элемент внутри после значения поля
            @type string | html
          -->
          <cds-icon v-if="!!appendInnerIcon" :name="appendInnerIcon" :size="size" />
          <slot name="append-inner" />
        </div>
      </template>
      <template v-if="hasAppend" #append>
        <!--
          @slot Элемент после значения поля
          @type string | html
        -->
        <slot name="append" />
      </template>
      <template #messages>
        <!--
          @slot Сообщения и подсказки
          @type string | html
        -->
        <slot name="messages" />
      </template>
    </cds-input>
    <cds-input-counter v-if="counter || limit" :current="counterValue" :max="limit" />
  </div>
</template>

<style lang="scss">
@import './CdsTextInput';
</style>
